// src/components/WorkflowViewer.jsx
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import HtmlNode from './HtmlNode';
import FormNode from './FormNode';
import PhotoCaptureNode from './PhotoCaptureNode';
import { useWorkflowCache } from '../contexts/WorkflowCacheContext';

/**
 * WorkflowViewer renders a given workflow (an array of views)
 * and dispatches to appropriate node types based on view.type.
 *
 * Props:
 *  - workflow: array of view objects (workflow steps)
 *  - preview_mode: boolean to indicate read-only preview mode
 *  - task_id: task identifier
 *  - accessKeyToken: string access key
 *  - onNavigate: callback to change workflow view
 *  - onFormValidChange: callback when form validity changes
 *  - closeTask: when set and current step is a leaf, render Close task in the same action row as step buttons
 */
export default function WorkflowViewer({
  workflow,
  preview_mode,
  task_id,
  accessKeyToken,
  onNavigate,
  onFormValidChange,
  closeTask,
}) {
  // Field-engineer routes use :view_id; admin preview uses :viewId — support both.
  const { view_id: viewIdSnake, viewId: viewIdCamel } = useParams();
  const viewId = viewIdSnake ?? viewIdCamel;
  const [currentView, setCurrentView] = useState(null); // State to track the current view
  const { recordStepVisit } = useWorkflowCache();

  useEffect(() => {
    if (workflow) {
      const selectedView = workflow.find((view) => view.id === viewId) || workflow[0];
      setCurrentView(selectedView);
    }
  }, [viewId, workflow]);

  useEffect(() => {
    if (preview_mode || !currentView?.id || !accessKeyToken || !task_id) return;
    recordStepVisit({
      accessKey: accessKeyToken,
      taskId: task_id,
      stepId: currentView.id,
    });
  }, [preview_mode, currentView?.id, accessKeyToken, task_id, recordStepVisit]);

  if (!workflow) {
    return <div className="container mt-5">Workflow not loaded.</div>;
  }

  if (!currentView) {
    return <div className="container mt-5">View not found.</div>;
  }

  // Determine view type (default to 'html' for backward compatibility)
  const viewType = (currentView.type || 'html').toLowerCase();

  const isLastNode =
    !currentView?.actions || currentView.actions.length === 0;
  const closeTaskSlot =
    closeTask && isLastNode && !preview_mode ? closeTask : null;

  // Render appropriate node type
  if (viewType === 'form') {
    return (
      // eslint-disable-next-line jsx-a11y/no-access-key -- domain prop: access-key token, not HTML accesskey
      <FormNode
        key={currentView.id}
        view={currentView}
        accessKey={accessKeyToken}
        taskId={task_id}
        onNavigate={onNavigate}
        onFormValidChange={onFormValidChange}
        closeTask={closeTaskSlot}
      />
    );
  }

  if (viewType === 'photo') {
    return (
      // eslint-disable-next-line jsx-a11y/no-access-key -- domain prop: access-key token, not HTML accesskey
      <PhotoCaptureNode
        key={currentView.id}
        view={currentView}
        accessKey={accessKeyToken}
        taskId={task_id}
        previewMode={preview_mode}
        onNavigate={onNavigate}
        onFormValidChange={onFormValidChange}
        closeTask={closeTaskSlot}
      />
    );
  }

  return (
    // eslint-disable-next-line jsx-a11y/no-access-key -- domain prop: access-key token, not HTML accesskey
    <HtmlNode
      key={currentView.id}
      view={currentView}
      accessKey={accessKeyToken}
      taskId={task_id}
      onNavigate={onNavigate}
      closeTask={closeTaskSlot}
    />
  );
}